import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import { listActivities, deleteActivity, type Activity, type ActivityFilters } from '@/api/activityApi';
import { useAuth } from '@/hooks/useAuth';

const ACTIVITY_TYPES = ['call', 'email', 'meeting', 'note', 'task'] as const;
const ACTIVITY_STATUSES = ['planned', 'in_progress', 'completed', 'cancelled'] as const;
const RELATED_TO_TYPES = ['customer', 'contact', 'opportunity'] as const;

export default function ActivityListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<ActivityFilters>({});

  const canWrite = hasPermission('activities', 'write');
  const canDelete = hasPermission('activities', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof ActivityFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => listActivities(params, filters),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('activities.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteActivity(id);
            message.success(t('activities.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const typeOptions = useMemo(
    () => ACTIVITY_TYPES.map((v) => ({ value: v, label: t(`activities.type_options.${v}`) })),
    [t]
  );

  const statusOptions = useMemo(
    () => ACTIVITY_STATUSES.map((v) => ({ value: v, label: t(`activities.status_options.${v}`) })),
    [t]
  );

  const relatedToTypeOptions = useMemo(
    () => RELATED_TO_TYPES.map((v) => ({ value: v, label: t(`activities.related_to_options.${v}`) })),
    [t]
  );

  const columns: ColumnsType<Activity> = useMemo(
    () => [
      {
        title: t('activities.field.subject'),
        dataIndex: 'subject',
        key: 'subject',
        sorter: true,
        ellipsis: true,
        render: (subject: string, record: Activity) => (
          <a onClick={() => navigate(`/activities/${record.id}`)}>{subject}</a>
        ),
      },
      {
        title: t('activities.field.type'),
        dataIndex: 'type',
        key: 'type',
        width: 110,
        render: (val: string) => {
          const typeColorMap: Record<string, string> = {
            call: 'blue',
            email: 'purple',
            meeting: 'green',
            note: 'gold',
            task: 'cyan',
          };
          return <Tag color={typeColorMap[val] ?? 'default'}>{t(`activities.type_options.${val}`)}</Tag>;
        },
      },
      {
        title: t('activities.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (val: string) => {
          const colorMap: Record<string, string> = {
            planned: 'blue',
            in_progress: 'orange',
            completed: 'green',
            cancelled: 'default',
          };
          return <Tag color={colorMap[val] ?? 'default'}>{t(`activities.status_options.${val}`)}</Tag>;
        },
      },
      {
        title: t('activities.field.related_to_type'),
        dataIndex: 'related_to_type',
        key: 'related_to_type',
        width: 120,
        render: (val: string) => t(`activities.related_to_options.${val}`),
      },
      {
        title: t('activities.field.scheduled_at'),
        dataIndex: 'scheduled_at',
        key: 'scheduled_at',
        sorter: true,
        width: 170,
        render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('activities.field.duration'),
        dataIndex: 'duration',
        key: 'duration',
        width: 100,
        sorter: true,
        render: (val: number) => (val > 0 ? `${val} ${t('activities.duration_unit')}` : '-'),
      },
      {
        title: t('activities.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 150,
        render: (_: unknown, record: Activity) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/activities/${record.id}`)}
            />
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/activities/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('activities.filter.type')}
        style={{ width: 140 }}
        allowClear
        options={typeOptions}
        onChange={(val) => handleFilterChange('type', val || undefined)}
      />
      <Select
        placeholder={t('activities.filter.status')}
        style={{ width: 140 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
      <Select
        placeholder={t('activities.filter.related_to_type')}
        style={{ width: 160 }}
        allowClear
        options={relatedToTypeOptions}
        onChange={(val) => handleFilterChange('related_to_type', val || undefined)}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/activities/new')}
        >
          {t('activities.new_activity')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('activities.list_title')}</h2>
      <DataGrid<Activity>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('activities.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/activities/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
